import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { serviceTypes } from "@/lib/mockData";

export default function CreateService() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    service_type: "tradfallning",
    price_model: "fast",
    base_price: "",
    travel_fee: "",
    availability: "",
  });

  useEffect(() => {
    if (!user) return;
    supabase
      .from("provider_profiles")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => { if (data) setProviderId(data.id); });
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerId) {
      toast.error("Du måste skapa en leverantörsprofil först");
      navigate("/skapa-leverantorsprofil");
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("services").insert({
      provider_id: providerId,
      title: form.title,
      description: form.description,
      service_type: form.service_type,
      price_model: form.price_model,
      base_price: parseFloat(form.base_price) || 0,
      travel_fee: form.travel_fee || null,
      availability: form.availability || null,
    });
    setLoading(false);
    if (error) {
      toast.error("Kunde inte skapa tjänst: " + error.message);
    } else {
      toast.success("Tjänst skapad!");
      navigate("/dashboard");
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container max-w-lg flex-1 py-8">
        <h1 className="mb-6 font-display text-2xl font-bold">Skapa tjänst</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Titel</Label>
            <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} required />
          </div>
          <div>
            <Label>Beskrivning</Label>
            <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} rows={4} />
          </div>
          <div>
            <Label>Tjänstetyp</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm(p => ({ ...p, service_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {serviceTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prismodell</Label>
              <Select value={form.price_model} onValueChange={(v) => setForm(p => ({ ...p, price_model: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fast">Fast pris</SelectItem>
                  <SelectItem value="timme">Timpris</SelectItem>
                  <SelectItem value="från">Från-pris</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Grundpris (kr)</Label>
              <Input type="number" value={form.base_price} onChange={(e) => setForm(p => ({ ...p, base_price: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Reseersättning</Label>
            <Input value={form.travel_fee} onChange={(e) => setForm(p => ({ ...p, travel_fee: e.target.value }))} placeholder="T.ex. Ingår inom 30 km" />
          </div>
          <div>
            <Label>Tillgänglighet</Label>
            <Input value={form.availability} onChange={(e) => setForm(p => ({ ...p, availability: e.target.value }))} placeholder="T.ex. Nästa vecka" />
          </div>
          <Button className="w-full" type="submit" disabled={loading}>
            {loading ? "Skapar…" : "Publicera tjänst"}
          </Button>
        </form>
      </div>
      <Footer hideOnMobile />
    </div>
  );
}
